import React, { useEffect, useRef, useState } from "react"
import { UserTimesheetType } from "modules/spending/timesheet/models/user-timesheet"
import styles from "./TimesheetUserRow.module.scss"
import { EmployerName } from "modules/common/components/employer/EmployerName"
import { observer } from "mobx-react"
import { TimesheetCell, DaySelector, TimesheetCellMode, OvertimeCell } from "./TimesheetCell"
import { OrderDictionaryType } from "modules/orders-manage/models/order-dictionary"
import { TimesheetInput, TimesheetSaver } from "./TimesheetInput"
import { getSnapshot } from "mobx-state-tree"
import { makeTimesheetInputStore } from "modules/spending/timesheet/models/timesheet-store"
import {
  TasksDictionaryType,
  WorkloadDayStoreType,
  WorkloadDayType,
} from "modules/spending/workload/models/workload-day"
import { ReactMouseSelect } from "react-mouse-select"
import { apiUrls } from "modules/common/services/communication/urls"
import { CopyDay, CopySource, findCopyDay, previousMonth } from "modules/spending/timesheet/services/copy-previous"

type MonthUsers = { user?: { id?: string }; days?: CopyDay[] }[]

// «Как в прошлый день» - только в своём дне (как в мобильной версии): прошлый месяц - лёгкий запрос
// onlyMine=true, сервер отдаёт табель самого пользователя. Чужой месяц так не получить: отбор по employerIds
// сервер при праве «прошлые периоды» подменяет на самого пользователя, а запрос всех сотрудников тяжёлый.
// Один запрос на месяц за сессию страницы
const previousMonths = new Map<string, Promise<MonthUsers>>()

export const TimesheetUserRow = observer((props: TimesheetUserRowProps) => {
  const {
    timesheet,
    onSave,
    selectedCells,
    onSelectCells,
    onSelectCell,
    tasks,
    selectedCell,
    multiSelectCells,
    onContext,
    isMainPage,
    light, selectedOrderId,
    ...selector
  } = props

  const [formStore, setformStore] = useState<WorkloadDayStoreType | null>(null)
  // источник «Как в прошлый день» для открытого пустого дня
  const [copySource, setCopySource] = useState<CopySource | null>(null)
  // идёт догрузка прошлого месяца для «Как в прошлый день» - кнопка показывает загрузку
  const [copyLoading, setCopyLoading] = useState(false)
  const editRun = useRef(0)

  const [cells, setcells] = useState<string[]>([])

  const selectCel = (str: string) => {

    const index = selectedCells
      ? selectedCells.findIndex((cell) => {
        return cell === str
      })
      : 0
    if (index === -1) {
      onSelectCells && onSelectCells([])
      onSelectCell(str)
    } else {
      onSelectCell(str)
    }
  }
  const onDayEditStart = (day: WorkloadDayType) => {
    const { orders, tasks } = props

    if (!day.isDayWorkable) {
      return
    }

    setformStore(makeTimesheetInputStore(day, getSnapshot(orders.orders), getSnapshot(tasks.tasks)))
    setCopySource(null)
    setCopyLoading(false)
    const run = ++editRun.current

    // «Как в прошлый день» - для своего пустого дня, который можно править. Уже заполненный (сохранённый) день
    // - без кнопки; если в открытом окне что-то добавили, кнопка остаётся и спросит: заменить или добавить.
    // Чужие дни (руководитель на странице табеля) - без кнопки
    if (!isDayEditable(day) || !isMine || day.units.length > 0) {
      return
    }

    const days = getSnapshot(timesheet.days) as unknown as CopyDay[]
    const found = findCopyDay(day.day, days)
    if (found) {
      setCopySource(found)
      return
    }

    // в начале месяца источник - в прошлом месяце: догружаем свой табель
    setCopyLoading(true)
    loadPreviousMonth(day.day).then((previous) => {
      if (editRun.current !== run) {
        return
      }
      setCopyLoading(false)
      previous.length && setCopySource(findCopyDay(day.day, [...previous, ...days]))
    })
  }

  const loadPreviousMonth = (dayString: string): Promise<CopyDay[]> => {
    const { year, month } = previousMonth(dayString)
    const userId = timesheet.user.id
    const key = `${year}:${month}`
    let request = previousMonths.get(key)

    if (!request) {
      request = timesheet.transport
        .get<{ users?: MonthUsers }>(apiUrls.timesheet.list, { params: { year, month, onlyMine: true } })
        .then((response: any) => (response?.users ?? []) as MonthUsers)
        .catch(() => {
          previousMonths.delete(key)
          return [] as MonthUsers
        })
      previousMonths.set(key, request)
    }

    return request.then((users) => users.find((u) => u?.user?.id === userId)?.days ?? [])
  }

  const isMine = timesheet.user.login === props.userLogin

  // Прошлый месяц - заранее, в фоне: если в текущем месяце до сегодня нет дня с работой, кнопке «Как в прошлый
  // день» в начале месяца нужен прошлый, и без предзагрузки она появлялась через несколько секунд после открытия
  useEffect(() => {
    if (!isMine || !timesheet.days.length) {
      return
    }
    const days = getSnapshot(timesheet.days) as unknown as CopyDay[]
    const today = timesheet.days.find((d) => d.isToday())
    const target = today ? today.day : days[days.length - 1].day
    if (!findCopyDay(target, days)) {
      loadPreviousMonth(days[0].day)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMine, timesheet.year, timesheet.month])

  const onDayEditStop = () => {
    editRun.current++
    setCopySource(null)
    setCopyLoading(false)
    setformStore(null)
  }

  const isDayEditable = (day: WorkloadDayType) => {
    const { canManage, userLogin } = props

    return day.isDayEditable(canManage, userLogin)
  }

  let timesheetDay: any
  const selectorRef = useRef<HTMLDivElement>(null)
  const itemClassName = "mouse-select__selectable"
  // ранг показываем «на тот период»: только если в этом месяце есть доплата за ранг,
  // с процентом именно этого месяца (история: у кого-то 8% в одних месяцах и 15% в других)
  const baseGrade = timesheet.user.grade
  const monthGrade =
    baseGrade && timesheet.gradePercent > 0
      ? { guid: baseGrade.guid, gradeName: baseGrade.gradeName, percent: timesheet.gradePercent }
      : null
  return (
    <div className={`timesheet-user-row ${styles.row}`} style={isMainPage ? { marginBottom: "5px" } : {}}>
      <div className="timesheet-user-name">
        <EmployerName name={timesheet.periodName || timesheet.user.name} position={timesheet.user.position} grade={monthGrade as any} />
      </div>
      <ReactMouseSelect
        containerRef={selectorRef}
        itemClassName={itemClassName}
        startSelectionCallback={(e: MouseEvent) => {
          onSelectCells && onSelectCells([])
          setcells([])
          onSelectCell("")
        }}
        finishSelectionCallback={(items: Element[], e: MouseEvent) => {
          const selectedItems = items.map((item) => item.getAttribute("data-id") || "")
          if (selectedItems.length > 0) {
            onSelectCells && onSelectCells(selectedItems)
            setcells(selectedItems)
            onSelectCell("")
          }
        }}
      />
      <div ref={selectorRef} className="timesheet-user-days" style={{ display: "flex" }}>
        {timesheet.days.map((day, index) => {
          const next = index + 1 < timesheet.days.length ? timesheet.days[index + 1] : undefined
          const preToday = !!next && next.isToday()
          timesheetDay = day
          return (
            <TimesheetCell
              key={day.day}
              day={day}
              preToday={preToday}
              onEdit={onDayEditStart}
              locked={!isDayEditable(day)}
              selectedCells={selectedCells}
              cells={cells}
              onSelectCel={selectCel}
              selectedCell={selectedCell}
              onContext={onContext}
              light={light}
              selectedOrderId={selectedOrderId}
              {...selector}
            />
          )
        })}
      </div>

      <OvertimeCell state={timesheet.hoursDiff} />

      {formStore && (
        <TimesheetInput
          store={formStore}
          isOpened={true}
          onClose={onDayEditStop}
          onSave={onSave}
          day={timesheetDay}
          selectedCells={selectedCells}
          readOnly={!isDayEditable(timesheetDay)}
          copySource={copySource}
          copyLoading={copyLoading}
          {...selector}
        />
      )}
    </div>
  )
})

interface TimesheetUserRowProps extends DaySelector, TimesheetSaver {
  timesheet: UserTimesheetType
  orders: OrderDictionaryType
  canManage: boolean
  mode: TimesheetCellMode
  userLogin: string
  tasks: TasksDictionaryType
  showMinutes: boolean
  selectedCells?: string[]
  onSelectCells?: (cell: string[]) => void
  onContext: (x: number, y: number, day: WorkloadDayType) => void
  multiSelectCells?: boolean
  isMainPage?: boolean
  light?: string
  selectedOrderId?: string
}

interface TimesheetUserRowState {
  formStore: WorkloadDayStoreType | null
  selectedItems: string[]
  mouseDown: boolean
}
